import * as Api from '@ViewModels';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { SortOptions } from '../../components/LeaderboardComponents/components/ListHeader';
import { IPrizeLogFilter } from '../../models/slotMachines';
import { ILeaderboard, ILeaderboardDemos } from '../../viewModels/gamification';
import {
	ILeaderboardDemosFilter,
	ILeaderboardFilter,
	LeaderboardDemosGroupBy,
	leaderboardDemosFilterToCriteria,
	leaderboardFilterToCriteria,
	prizeLogFilterToGameTokenFilter,
} from './criteria';

/**
 * The board polls to stay live; each poll keeps the previous answer on screen until the new one lands. The queries log
 * errors but don't toast them: a toast per failed poll would repeat every 30 seconds, and the tabs show the error inline.
 */
const LEADERBOARD_POLL_MS = 30_000;

const pollingOptions = {
	placeholderData: keepPreviousData,
	refetchInterval: LEADERBOARD_POLL_MS,
	refetchOnWindowFocus: false,
} as const;

export function useLeaderboardQuery({
	asc,
	filter,
	sortBy,
}: {
	asc: boolean;
	filter: ILeaderboardFilter;
	sortBy: SortOptions;
}) {
	const userSession = useUserSession();
	return useQuery({
		...pollingOptions,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ILeaderboard>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { sort: asc ? 'asc' : 'desc', sortBy },
					urlPath: 'userDailyActivity/leaderboard/filter',
				}),
				'POST',
				{ criteria: leaderboardFilterToCriteria(filter) }
			),
		queryKey: ['userDailyActivity/leaderboard/filter', userSession.account?.id, filter, sortBy, asc],
		meta: {
			errorLogging: { fn: 'useLeaderboardQuery' },
		},
	});
}

export function useLeaderboardDemosQuery({
	filter,
	groupBy,
}: {
	filter: ILeaderboardDemosFilter;
	/** Undefined while the account's default grouping is still being worked out; the query waits for it. */
	groupBy: LeaderboardDemosGroupBy | undefined;
}) {
	const userSession = useUserSession();
	return useQuery({
		...pollingOptions,
		enabled: groupBy !== undefined,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<ILeaderboardDemos>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { groupBy },
					urlPath: 'userDailyActivity/leaderboard/demos/filter',
				}),
				'POST',
				{ criteria: leaderboardDemosFilterToCriteria(filter) }
			),
		queryKey: ['userDailyActivity/leaderboard/demos/filter', userSession.account?.id, filter, groupBy],
		meta: {
			errorLogging: { fn: 'useLeaderboardDemosQuery' },
		},
	});
}

export function useLeaderboardPrizesQuery({ filter }: { filter: IPrizeLogFilter }) {
	const userSession = useUserSession();
	return useQuery({
		...pollingOptions,
		queryFn: async () => {
			const page = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IPrizeLog>>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'game/prize-log/filter' }),
				'POST',
				prizeLogFilterToGameTokenFilter(filter)
			);
			return page?.values ?? [];
		},
		queryKey: ['game/prize-log/filter', userSession.account?.id, filter],
		meta: {
			errorLogging: { fn: 'useLeaderboardPrizesQuery' },
		},
	});
}
